import { ChevronDown, Plus, Trash2 } from "lucide-react";
import { useState } from "react";

const defaultCriteria = [
  { id: "clarity", name: "Clarity" },
  { id: "impact", name: "Impact" },
  { id: "likelihood", name: "Likelihood" },
  { id: "urgency", name: "Urgency" },
];

function TeamScore({ team, scores, onChange, accent }) {
  return (
    <div
      className={`team-score ${accent}`}
      data-testid={`${accent}-score-panel`}
    >
      <div className="team-score-title">
        <span className="team-dot"></span>
        <div>
          <p>{team}</p>
          <small>Your 1–5 comparison</small>
        </div>
      </div>
      {defaultCriteria.map((criterion) => (
        <div className="criteria-row" key={criterion.id}>
          <div>
            <label htmlFor={`${accent}-${criterion.id}`}>{criterion.name}</label>
          </div>
          <output data-testid={`${accent}-${criterion.id}-value`}>
            {scores[criterion.id] || 3}
          </output>
          <input
            id={`${accent}-${criterion.id}`}
            type="range"
            min="1"
            max="5"
            value={scores[criterion.id] || 3}
            onChange={(event) => onChange(criterion.id, Number(event.target.value))}
            data-testid={`${accent}-${criterion.id}-slider`}
          />
        </div>
      ))}
    </div>
  );
}

export default function ScorePanel({
  customCriteria,
  teams,
  proScores,
  conScores,
  onAddCriterion,
  onCustomCriterionChange,
  onRemoveCriterion,
  onScoreChange,
}) {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <section
      className="scoring-section section-reveal delay-two"
      data-testid="scoring-section"
    >
      <div className="section-heading">
        <div>
          <p className="eyebrow">Deliberation</p>
          <h2>Round Comparison</h2>
        </div>
        <button
          aria-expanded={isOpen}
          className="comparison-toggle"
          onClick={() => setIsOpen((current) => !current)}
          data-testid="round-comparison-toggle"
        >
          {isOpen ? "Hide comparison" : "Open comparison"}
          <ChevronDown
            size={17}
            className={isOpen ? "comparison-chevron open" : "comparison-chevron"}
          />
        </button>
      </div>
      {isOpen && (
        <div className="comparison-content" data-testid="round-comparison-content">
          <p className="comparison-subtitle" data-testid="round-comparison-subtitle">
            Optional tools to help organize your own comparison of the round.
            These ratings do not determine the ballot.
          </p>
          <div className="score-grid">
            <TeamScore
              team={teams.pro}
              scores={proScores}
              onChange={(key, value) => onScoreChange("pro", key, value)}
              accent="pro"
            />
            <TeamScore
              team={teams.con}
              scores={conScores}
              onChange={(key, value) => onScoreChange("con", key, value)}
              accent="con"
            />
          </div>
          <div className="custom-criteria" data-testid="custom-criteria-list">
            <div className="custom-criteria-heading">
              <div>
                <p className="eyebrow">Your criteria</p>
                <h3>Additional comparisons</h3>
              </div>
              <button
                className="add-criterion-button"
                onClick={onAddCriterion}
                data-testid="add-custom-criterion-button"
              >
                <Plus size={16} />
                Add Criterion
              </button>
            </div>
            {customCriteria.map((criterion) => (
              <div className="custom-criterion-row" key={criterion.id}>
                <div className="custom-criterion-name-field">
                  <label htmlFor={`custom-name-${criterion.id}`}>Criterion</label>
                  <input
                    id={`custom-name-${criterion.id}`}
                    onChange={(event) =>
                      onCustomCriterionChange(
                        criterion.id,
                        "name",
                        event.target.value,
                      )
                    }
                    placeholder="Name this comparison"
                    maxLength={100}
                    value={criterion.name}
                    data-testid={`custom-criterion-name-${criterion.id}`}
                  />
                </div>
                <label htmlFor={`custom-pro-${criterion.id}`}>Pro</label>
                <output data-testid={`custom-criterion-pro-value-${criterion.id}`}>
                  {criterion.pro_score}
                </output>
                <input
                  id={`custom-pro-${criterion.id}`}
                  max="5"
                  min="1"
                  onChange={(event) =>
                    onCustomCriterionChange(
                      criterion.id,
                      "pro_score",
                      Number(event.target.value),
                    )
                  }
                  type="range"
                  value={criterion.pro_score}
                  className="custom-pro-slider"
                  data-testid={`custom-criterion-pro-slider-${criterion.id}`}
                />
                <label htmlFor={`custom-con-${criterion.id}`}>Con</label>
                <output data-testid={`custom-criterion-con-value-${criterion.id}`}>
                  {criterion.con_score}
                </output>
                <input
                  id={`custom-con-${criterion.id}`}
                  max="5"
                  min="1"
                  onChange={(event) =>
                    onCustomCriterionChange(
                      criterion.id,
                      "con_score",
                      Number(event.target.value),
                    )
                  }
                  type="range"
                  value={criterion.con_score}
                  className="custom-con-slider"
                  data-testid={`custom-criterion-con-slider-${criterion.id}`}
                />
                <button
                  aria-label="Remove custom criterion"
                  className="remove-criterion-button"
                  onClick={() => onRemoveCriterion(criterion.id)}
                  title="Remove criterion"
                  data-testid={`remove-custom-criterion-${criterion.id}`}
                >
                  <Trash2 size={16} />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </section>
  );
}
